import {
    useGetPermissions,
    useUser,
    useGetLeads,
    useProjects,
    useTotalStaff
} from "../Use-auth";
import { useMemo } from "react";
import moment from "moment";
import { useDateRange } from "../Pages/DateRangeContext";
import { Link } from "wouter";
import ChartCard from "./ChartCard";
import Card from "./Cards";

import {
    AlignStartVertical,
    EllipsisVertical,
    TrendingUp,
    DollarSign,

    UserRound,
    Volleyball,
    Star,
} from "lucide-react";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    LineChart,
    Line,
    LabelList,
    Cell
} from "recharts";
import { getStatusLabelColor } from "../Pages/utils/taskUtils";

const chartConfigs = {
    daily: {
        projects: {
            title: "Today's Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects today"
        },
        sales: {
            title: "Today's Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads today"
        },

    },

    weekly: {
        projects: {
            title: "Weekly Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects this week"
        },
        sales: {
            title: "Weekly Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads this week"
        },
    },

    monthly: {
        projects: {
            title: "Monthly Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects this month"
        },
        sales: {
            title: "Monthly Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads this month"
        },
    },

    yearly: {
        projects: {
            title: "Yearly Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects this year"
        },
        sales: {
            title: "Yearly Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads this year"
        },
    }
};

const getLabelByRange = (date, rangeType) => {
    if (rangeType === "daily") return "Today";
    if (rangeType === "weekly") return date.format("ddd");
    if (rangeType === "monthly") return date.format("D");
    if (rangeType === "yearly") return date.format("MMM");
};

export default function ManagementPage() {
    const { start, end, rangeType } = useDateRange();
    const { data: totalStaff = [] } = useTotalStaff();
    const { data: projects = [] } = useProjects();
    const { data: leadData } = useGetLeads();
    const { data: user } = useUser();
    const { data: existingPermissions } = useGetPermissions();

    const response1 = Array.isArray(leadData) ? leadData : leadData?.data || [];

    const allWonLeads = Array.isArray(response1) ? response1 : [];

    console.log(leadData)

    const isWithinRange = (date) => {
        if (!start || !end) return true;
        const projectDate = moment(date);
        return projectDate.isBetween(moment(start), moment(end), undefined, '[]');
    };

    const filteredProjects = useMemo(() => {
        return projects.filter((p) =>
            isWithinRange(p.createdAt || p.updatedAt)
        );
    }, [projects, start, end]);

    const filteredLeads = useMemo(() => {
        return allWonLeads.filter(l => {
            if (!l?.createdAt) return false;

            const status = l.leadStatus?.toLowerCase();
            return status === "won" && isWithinRange(l.createdAt);
        });
    }, [allWonLeads, start, end]);

    const chartData = useMemo(() => {
        let data = [];

        if (rangeType === "daily") {
            data = [{ label: "Today", total: 0, active: 0, lost: 0, leadsWon: 0, leadsLost: 0, employees: 0 }];
        } else if (rangeType === "weekly") {
            const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            data = days.map(day => ({
                label: day,
                total: 0,
                active: 0,
                lost: 0,
                leadsWon: 0,
                leadsLost: 0,
                employees: 0
            }));
        } else if (rangeType === "monthly") {
            const daysInMonth = moment(start).daysInMonth();
            data = Array.from({ length: daysInMonth }, (_, i) => ({
                label: (i + 1).toString(),
                total: 0,
                active: 0,
                lost: 0,
                leadsWon: 0,
                leadsLost: 0,
                employees: 0
            }));
        } else if (rangeType === "yearly") {
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            data = months.map(m => ({
                label: m,
                total: 0,
                active: 0,
                lost: 0,
                leadsWon: 0,
                leadsLost: 0,
                employees: 0
            }));
        }

        data.forEach(item => {
            filteredProjects.forEach(p => {
                const date = moment(p.updatedAt || p.createdAt);
                const key = getLabelByRange(date, rangeType);
                if (key === item.label) {
                    item.total++;
                    if (p.status === "Lost") item.lost++;
                    else item.active++;
                }
            });
        });

        filteredLeads.forEach(l => {
            const key = getLabelByRange(moment(l.createdAt), rangeType);
            const item = data.find(d => d.label === key);
            if (item) {
                if (l.leadStatus === "Won") item.leadsWon += 1;
                if (l.leadStatus === "Lost") item.leadsLost += 1;
            }
        });

        totalStaff.forEach(e => {
            if (!e?.createdAt) return;
            const key = getLabelByRange(moment(e.createdAt), rangeType);
            const item = data.find(d => d.label === key);
            if (item) item.employees++;
        });

        return data;
    }, [filteredProjects, filteredLeads, totalStaff, rangeType, start, end]);

    const isHRorAdmin = user?.department === "Admin" || user?.department === "HR";
    const currentUserPermissions = isHRorAdmin
        ? { management: { home_view: true } }
        : existingPermissions?.find((p) => p.userId === user?.userId);
    const canViewHome = isHRorAdmin || currentUserPermissions?.management?.home_view;

    // const hasActiveTasks = (tasks = {}) => {
    //     const pending = tasks.pending?.length || 0;
    //     const progress =
    //         tasks.in_progress?.length || tasks.progress?.length || 0;
    //     const testing = tasks.testing?.length || 0;

    //     return pending > 0 || progress > 0 || testing > 0;
    // };

    const filteredActiveProjects = filteredProjects.filter(
        p => p.status === "In Progress"
    );

    const currentChart = chartConfigs[rangeType] || chartConfigs.daily;
    const filteredLostProjects = filteredProjects.filter(p => p.status === "Lost");

    const filteredWonLeads = allWonLeads.filter(lead => {
        if (!lead?.createdAt) return false;

        const status = lead.leadStatus?.toLowerCase();
        return status === "won" && isWithinRange(lead.createdAt);
    });
    
    const allEmployees = totalStaff || [];
    const activeEmployees = allEmployees.filter(
        (e) => e.employeeStatus === "Active"
    );

    return (
        <div className=" p-2 rs:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
                <Link href="/projects?tab=list&filter=all">
                    <Card
                        title="Total Projects"
                        value={canViewHome ? filteredProjects.length : "0"}
                        icon={<AlignStartVertical className="w-12 h-12 opacity-80" />}
                        bg="from-blue-500 to-blue-600"
                    />
                </Link>

                <Link href="/projects?tab=list&filter=In%20Progress">
                    <Card
                        title="Active Projects"
                        value={canViewHome ? filteredActiveProjects.length : "0"}
                        icon={<Volleyball className="w-12 h-12 opacity-80" />}
                        bg="from-green-500 to-green-600"
                    />
                </Link>

                <Link href="/projects?tab=list&filter=Lost">
                    <Card
                        title="Lost Projects"
                        value={canViewHome ? filteredLostProjects.length : "0"}
                        icon={<Star className="w-12 h-12 opacity-80" />}
                        bg="from-purple-500 to-purple-600"
                    />
                </Link>

               <Link href="/myTeam"> <Card
                    title="Total Active Staff"
                    value={canViewHome ? activeEmployees.length : "0"}
                    icon={<UserRound className="w-12 h-12 opacity-80" />}
                    bg="from-orange-500 to-orange-600"
                /></Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-6">
                <div className="bg-white rounded-lg sm:rounded-2xl p-2 rs:p-6 shadow-lg border border-gray-100">
                    <div className="flex justify-between items-center mb-2 sm:mb-4">
                        <h3 className="font-bold text-[16px] sm:text-xl text-gray-800">Recent Projects</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs sm:text-sm">
                            <thead className="text-gray-600 border-b border-gray-200">
                                <tr>
                                    <th className="text-left px-2 sm:px-2 py-2">S/N</th>
                                    <th className="text-left px-2 sm:px-2 py-2">Project Name</th>
                                    <th className="px-2 sm:px-2 py-2 text-left">Status</th>
                                    <th className="text-left px-2 sm:px-2 py-2 hidden zs:table-cell">Date</th>
                                    <th className="text-left px-2 sm:px-2 py-2 hidden zx:table-cell">Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {canViewHome && filteredProjects.length > 0 ? (
                                    filteredProjects
                                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                        .slice(0, 10)
                                        .map((project, index) => {
                                            return (
                                                <tr key={project._id} className="hover:bg-gray-50 transition-colors border-b">
                                                    <td className="px-2 sm:px-2 py-2">{index + 1}</td>
                                                    <td className="px-2 sm:px-2 py-2 font-medium text-gray-800">{project.projectName}</td>
                                                    <td className="px-2 sm:px-2 py-2">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusLabelColor(project.status)}`}>
                                                            {project.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 sm:px-2 py-2 text-gray-600 hidden zs:table-cell">{moment(project.createdAt).format("DD MMM YYYY")}</td>
                                                    <td className="px-2 sm:px-2 py-2 text-gray-600 hidden zx:table-cell">
                                                        {project.isMultiProject ? "Multi Project" : "Single Project"}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-6 text-gray-400">No projects found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Leads Table */}
                <div className="bg-white rounded-lg sm:rounded-2xl p-2 rs:p-6 shadow-lg border border-gray-100">
                    <div className="flex justify-between items-center mb-2 sm:mb-4">
                        <h3 className="font-bold text-[16px] sm:text-xl text-gray-800">Leads</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs sm:text-sm">
                            <thead className="text-gray-600 border-b border-gray-200">
                                <tr>
                                    <th className="text-left px-2 sm:px-2 py-2">S/N</th>
                                    <th className="text-left px-2 sm:px-2 py-2">Client Name</th>
                                    <th className="px-2 sm:px-2 py-2 text-left">Status</th>
                                    <th className="text-left px-2 sm:px-2 py-2 hidden zs:table-cell">Date</th>
                                    <th className="text-left px-2 sm:px-2 py-2 hidden zx:table-cell">Lead Source</th>
                                    <th className="text-left px-2 sm:px-2 py-2">Budgeted</th>
                                </tr>
                            </thead>
                            <tbody>
                                {canViewHome && filteredWonLeads.length > 0 ? (
                                    <>
                                        {filteredWonLeads
                                            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                            .slice(0, 10)
                                            .map((lead, index) => {
                                                const totalBudget = (lead.projects || []).reduce(
                                                    (sum, project) => sum + (Number(project.budget) || 0),
                                                    0
                                                );
                                                return (
                                                    <tr key={lead._id || index} className="hover:bg-gray-50 transition-colors border-b">
                                                        <td className="px-2 sm:px-2 py-2">{index + 1}</td>
                                                        <td className="px-2 sm:px-2 py-2 font-medium text-gray-800 truncate max-w-[10px]">{lead.firstName} {lead.lastName}</td>
                                                        <td className="px-2 sm:px-2 py-2">
                                                            <span className={`px-2 py-1 sm:px-2 sm:py-2 rounded-full text-xs font-semibold ${lead.leadStatus === "Won" ? "bg-green-100 text-green-600" :
                                                                lead.leadStatus === "Lost" ? "bg-red-100 text-red-600" :
                                                                    "bg-gray-100 text-gray-600"
                                                                }`}>
                                                                {lead.leadStatus}
                                                            </span>
                                                        </td>
                                                        <td className="px-2 sm:px-2 py-2 text-gray-600 hidden zs:table-cell">{moment(lead.createdAt).format("DD MMM YYYY")}</td>
                                                        <td className="px-2 sm:px-2 py-2 text-gray-600 hidden zx:table-cell">{lead.projects?.[0]?.leadSource || '-'}</td>
                                                        <td className="px-2 sm:px-2 py-2 text-gray-600">${totalBudget}</td>
                                                    </tr>
                                                );
                                            })
                                        }

                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-6 text-gray-400">No Won Leads Found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3 sm:gap-6 mt-4 sm:mt-8">
                <ChartCard title={currentChart.projects.title} icon={currentChart.projects.icon} description={currentChart.projects.description}>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={canViewHome ? chartData : []} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} width={30} />
                            <Tooltip />
                            <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} name="Total Projects"></Line>
                            <Line type="monotone" dataKey="active" stroke="#22C55E" strokeWidth={2} name="Active Projects"></Line>
                            <Line type="monotone" dataKey="lost" stroke="#EF4444" strokeWidth={2} name="Lost Projects"></Line>
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title={currentChart.sales.title} icon={currentChart.sales.icon} description={currentChart.sales.description}>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart
                            data={canViewHome ? chartData : []}
                            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} width={30} />
                            <Tooltip />
                            <Line type="monotone" dataKey="leadsWon" stroke="#10B981" strokeWidth={2} name="Won Leads" />
                            <Line type="monotone" dataKey="leadsLost" stroke="#EF4444" strokeWidth={2} name="Lost Leads" />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

            </div>
        </div>
    );
}
